import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    QueryList
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { ScrollbarComponent } from 'ngx-scrollbar';
import { Subject } from 'rxjs';
import { DetectBrowserService } from '../../services/detect-browser/detect-browser.service';
import { ErrorMessageHelper } from '../../utilities/error-message.i18n.helper';
import { Utilities } from '../../utilities/utilities.helper';

@Component({
    selector: 'base-form-control-component',
    template: '',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BaseFormControlComponent),
            multi: true
        }
    ]
})
export class BaseFormControlComponent<T> implements ControlValueAccessor, OnDestroy {
    @Input() public control?: FormControl;
    @Input() public group?: FormGroup;
    @Input() public formControlName?: string;
    @Input() public disabled?: boolean;
    public hasCorrectedPrematureDirty = false;

    private _model: T;
    protected modelChangeSubject = new Subject();

    public cdr: ChangeDetectorRef;
    public elementRef: ElementRef;
    public detectBrowserService: DetectBrowserService;
    public errorMessageHelper: ErrorMessageHelper;
    public i18n: I18n;
    public highlightedOptionIndex: number;

    constructor(public injector: Injector) {
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.elementRef = this.injector.get(ElementRef);
        this.errorMessageHelper = this.injector.get(ErrorMessageHelper);
        this.detectBrowserService = this.injector.get(DetectBrowserService);
        this.i18n = this.injector.get(I18n);
    }

    public propagateChange = (_: T) => {};
    public propagateTouch = () => {};

    public get model(): T {
        return this._model;
    }
    public set model(value: T) {
        this._model = value;
    }

    public ngOnDestroy() {
        this.cdr.detach();
    }

    public handleChange(value?: T): void {
        this.model = value;
        this.propagateChange(this.model);
        this.propagateTouch();
    }

    public getControl(): AbstractControl | null {
        if (this.control) {
            return this.control;
        } else if (this.formControlName) {
            return this.group ? this.group.get(this.formControlName) : null;
        }
        return null;
    }

    public showErrorMessage(shouldValidate = true): boolean {
        const control = this.getControl();
        const controlHasBeenTouched = this.detectBrowserService.browserPrematurelyDirtiesInputs()
            ? control && control.touched && this.hasCorrectedPrematureDirty
            : control && control.touched;
        return control
            ? control.invalid && controlHasBeenTouched && shouldValidate && !!this.getErrorMessage()
            : false;
    }

    public getErrorMessage(): string {
        const control = this.getControl();
        if (control && control.invalid) {
            // Only get the error message if invalid state.
            const errors = Object.keys(control.errors);
            if (control.errors[errors[0]].message) {
                // If the error object has a message, always show this.
                return control.errors[errors[0]].message;
            } else if (this.errorMessageHelper[errors[0]]) {
                // If we have a matching error key in the ErrorMessage dictionary...
                if (typeof this.errorMessageHelper[errors[0]] === 'function') {
                    // If the error message is a function, call it with appropriate parameters.
                    const errorObj = control.errors[errors[0]];
                    let param = '';
                    if (errors[0] === 'minLength' || errors[0] === 'maxLength') {
                        param = 'requiredLength';
                    } else if (errors[0] === 'min') {
                        param = 'min';
                    } else if (errors[0] === 'max') {
                        param = 'max';
                    }
                    return this.errorMessageHelper[errors[0]](errorObj[param]);
                }

                // Else return the error message.
                return this.errorMessageHelper[errors[0]];
            }
        }

        return '';
    }

    public setHighlightedOption(
        index: number,
        dropdownMenuRef: ElementRef,
        optionRefs: QueryList<ElementRef>,
        scrollRef: ScrollbarComponent
    ): void {
        this.highlightedOptionIndex = index;

        if (dropdownMenuRef && optionRefs && optionRefs.toArray()[this.highlightedOptionIndex]) {
            const dropdownBB = dropdownMenuRef.nativeElement.getBoundingClientRect();
            const highlightedBB = optionRefs
                .toArray()
                [this.highlightedOptionIndex].nativeElement.getBoundingClientRect();
            const padding = 10;

            if (highlightedBB.bottom + padding > dropdownBB.bottom) {
                // If the highlighted element dips below the bottom of the dropdown menu.
                scrollRef.scrollYTo(
                    (this.highlightedOptionIndex + 1) * highlightedBB.height -
                        dropdownBB.height +
                        2 * padding
                );
            } else if (highlightedBB.top < dropdownBB.top + padding) {
                // If the highlighted element goes above the top of the dropdown menu.
                scrollRef.scrollYTo(this.highlightedOptionIndex * highlightedBB.height);
            }
        }
    }

    /*
     * ControlValueAccessor implementation
     */
    public writeValue(model: T) {
        if (model !== undefined) {
            this.model = model;
            this.modelChangeSubject.next(this._model);
        }
    }
    public registerOnChange(fn: (_: T) => void) {
        this.propagateChange = fn;
    }
    public registerOnTouched(fn: () => void) {
        this.propagateTouch = fn;
    }

    public fixPrematurelyDirtiedInputs(): void {
        const control = this.getControl();

        if (this.detectBrowserService.browserPrematurelyDirtiesInputs() && control) {
            Utilities.executeAfterRender(() => {
                control.markAsPristine();
                control.markAsUntouched();
                this.cdr.detectChanges();
                Utilities.executeAfterRender(() => {
                    this.hasCorrectedPrematureDirty = true;
                });
            });
        }
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
